import Icon from "@/components/Icon/Icon";
import Input from "@/components/Input/Input";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { isNumber } from "@/utils/typeGuards";
import { memo, useCallback } from "react";
import type { FieldRenderInputProps } from "../../types";
import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";

/**
 * 数値フィールドのプロパティ
 */
interface ChangeNumberFieldProps extends BaseFieldProps {
  /** 最小値 */
  min?: number;

  /** 最大値 */
  max?: number;

  /** 増減幅（ステップ値） */
  step?: number;

  /** プレースホルダー */
  placeholder?: string;
}

/**
 * 数値入力フィールドコンポーネント
 * 整数値または小数値の入力に特化したフィールド
 */
export const ChangeNumberField = memo(
  ({
    fieldId,
    field,
    readOnly,
    disabled,
    className,
    min,
    max,
    step,
    "data-testid": testId,
  }: ChangeNumberFieldProps) => {
    // 入力レンダリング関数
    const renderInput = useCallback(
      (props: FieldRenderInputProps) => {
        const tc = useCommonTranslation();
        // 現在の値を表示用に変換
        const displayPreviousValue =
          props.previousValue === null || props.previousValue === undefined
            ? tc("unregistered")
            : String(props.previousValue);

        // 数値を文字列として扱う（空の入力を許可するため）
        const stringValue =
          props.value !== undefined && props.value !== null
            ? String(props.value)
            : "";

        // 入力変更ハンドラー
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;

          // 空の場合はundefinedを設定
          if (newValue === "") {
            props.onChange(undefined);
            return;
          }

          // 数値に変換（+演算子を使用）
          const numValue = +newValue;

          // 有効な数値の場合のみ値を更新
          if (isNumber(numValue)) {
            props.onChange(numValue);
          }
        };

        return (
          <HStack className="items-center justify-center w-full gap-4">
            {/* 現在の値（読み取り専用のテキスト表示） */}
            <div className="flex-1 justify-start min-w-0">
              <Text
                variant="labelSemiLarge"
                className="text-neutral-on-fill break-words"
              >
                {displayPreviousValue}
              </Text>
            </div>

            {/* Arrow Icon */}
            <div className="items-center flex-shrink-0 self-center pr-3 pl-3">
              <Icon
                iconName="ArrowBigRightIcon"
                className="w-4 h-4 text-primary-on-neutral-fill"
              />
            </div>

            {/* 新しい値の入力 */}
            <div className="flex-1 min-w-0">
              {props.readOnly ? (
                <Text
                  variant="labelSemiLarge"
                  className="text-neutral-on-fill break-words"
                >
                  {stringValue || tc("unregistered")}
                </Text>
              ) : (
                // 通常時は入力フィールドを表示
                <Input
                  id={props.id}
                  name={props.name}
                  type="number"
                  value={stringValue}
                  onChange={handleChange}
                  onFocus={props.onFocus}
                  onBlur={props.onBlur}
                  placeholder={props.placeholder}
                  disabled={props.disabled}
                  readOnly={props.readOnly}
                  required={props.required}
                  min={min}
                  max={max}
                  step={step}
                  error={!!props.error}
                  aria-invalid={!!props.error}
                  aria-describedby={
                    props.error ? `${props.id}-error` : undefined
                  }
                  width="100%"
                  className="w-full"
                  data-testid={props["data-testid"] || "number-field"}
                />
              )}
            </div>
          </HStack>
        );
      },
      [min, max, step],
    );

    // BaseFieldを使用して数値フィールドをレンダリング
    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        readOnly={readOnly}
        disabled={disabled}
        layout="vertical" // UI 的にラベルと入力フィールドが縦並びじゃないとしんどいので
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

ChangeNumberField.displayName = "ChangeNumberField";
